import { ref, get, set, push, remove } from './phpDataAdapter';
import { database } from '../config/backend';
import {
  Transaction,
  TransactionCategory,
  FinancialAccount,
  Budget,
  FinancialStats as ExpandedFinancialStats,
  CashFlowData,
  CategorySummary,
  MonthlyReport,
} from '../types/financial';

// Interfaces
export interface NotaFiscal {
  id: string;
  consultaId: string;
  pacienteId: string;
  pacienteNome: string;
  valor: number;
  dataEmissao: string;
  dataVencimento?: string | null;
  status: 'rascunho' | 'emitida' | 'cancelada';
  descricaoServico: string;
  emitente: {
    nome: string;
    cpf?: string | null;
    cnpj?: string | null;
    endereco?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    cep?: string | null;
    telefone?: string | null;
    email?: string | null;
    inscricaoEstadual?: string | null;
    crn?: string | null;
  };
  cliente: {
    nome: string;
    cpf?: string | null;
    cnpj?: string | null;
    endereco?: string | null;
    cidade?: string | null;
    estado?: string | null;
    cep?: string | null;
    telefone?: string | null;
    email?: string | null;
  };
  observacoes?: string | null;
  assinatura?: {
    nome?: string | null;
    data?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ReceitaIR {
  periodo: string; // YYYY-MM
  total: number;
  consultas: number;
  media: number;
  detalhes: Array<{
    data: string;
    pacienteNome: string;
    valor: number;
    tipo: 'primeira' | 'retorno';
  }>;
}

export interface FinancialSettings {
  emitente: NotaFiscal['emitente'];
  updatedAt: string;
}

// Funções auxiliares
function safeString(value: any, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function safeNumber(value: any, fallback = 0): number {
  if (value == null) return fallback;
  const num = Number(value);
  return isNaN(num) ? fallback : num;
}

// ===== Configurações Financeiras =====

export async function getFinancialSettings(
  nutritionistId: string
): Promise<FinancialSettings | null> {
  if (!nutritionistId) return null;

  try {
    const settingsRef = ref(database, `users/${nutritionistId}/settings/financial`);
    const snapshot = await get(settingsRef);
    
    if (!snapshot.exists()) return null;
    
    const data = snapshot.val();
    return {
      emitente: data.emitente || {},
      updatedAt: data.updatedAt || new Date().toISOString(),
    };
  } catch (error) {
    console.error('Erro ao buscar configurações financeiras:', error);
    return null;
  }
}

export async function saveFinancialSettings(
  nutritionistId: string,
  emitente: NotaFiscal['emitente']
): Promise<void> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  const settingsRef = ref(database, `users/${nutritionistId}/settings/financial`);
  await set(settingsRef, {
    emitente,
    updatedAt: new Date().toISOString(),
  });
}

// ===== Valores de Consulta =====

export async function getConsultationFinancial(
  nutritionistId: string,
  patientId: string,
  consultationId: string
): Promise<{ valor?: number; pago?: boolean; dataPagamento?: string } | null> {
  if (!nutritionistId || !patientId || !consultationId) return null;

  try {
    const financialRef = ref(
      database,
      `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
    );
    const snapshot = await get(financialRef);
    
    if (!snapshot.exists()) return null;
    
    const data = snapshot.val();
    return {
      valor: data.valor ? safeNumber(data.valor) : undefined,
      pago: data.pago === true,
      dataPagamento: data.dataPagamento || undefined,
    };
  } catch (error) {
    console.error('Erro ao buscar dados financeiros da consulta:', error);
    return null;
  }
}

export async function updateConsultationFinancial(
  nutritionistId: string,
  patientId: string,
  consultationId: string,
  data: {
    valor?: number;
    pago?: boolean;
    dataPagamento?: string;
  }
): Promise<void> {
  if (!nutritionistId || !patientId || !consultationId) {
    throw new Error('Dados inválidos');
  }

  const financialRef = ref(
    database,
    `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
  );

  const financialData: any = {};
  if (data.valor !== undefined) financialData.valor = data.valor;
  if (data.pago !== undefined) financialData.pago = data.pago;
  if (data.dataPagamento !== undefined) {
    financialData.dataPagamento = data.dataPagamento || null;
  }
  financialData.updatedAt = new Date().toISOString();

  await set(financialRef, financialData);
}

// ===== Notas Fiscais =====

// Função auxiliar para remover valores undefined (JSON não serializa campos indefinidos)
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
}

export async function createInvoice(
  nutritionistId: string,
  invoice: Omit<NotaFiscal, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  const invoicesRef = ref(database, `users/${nutritionistId}/invoices`);
  const newInvoiceRef = push(invoicesRef);
  const invoiceId = newInvoiceRef.key!;

  if (!invoiceId) throw new Error('Falha ao gerar ID da nota fiscal');

  const now = new Date().toISOString();
  const invoiceData = removeUndefined({
    ...invoice,
    id: invoiceId,
    createdAt: now,
    updatedAt: now,
  });

  await set(newInvoiceRef, invoiceData);

  return invoiceId;
}

export async function getInvoice(
  nutritionistId: string,
  invoiceId: string
): Promise<NotaFiscal | null> {
  if (!nutritionistId || !invoiceId) return null;

  try {
    const invoiceRef = ref(database, `users/${nutritionistId}/invoices/${invoiceId}`);
    const snapshot = await get(invoiceRef);
    
    if (!snapshot.exists()) return null;
    
    return snapshot.val() as NotaFiscal;
  } catch (error) {
    console.error('Erro ao buscar nota fiscal:', error);
    return null;
  }
}

export async function getInvoices(
  nutritionistId: string,
  limit: number = 50
): Promise<NotaFiscal[]> {
  if (!nutritionistId) return [];

  try {
    const invoicesRef = ref(database, `users/${nutritionistId}/invoices`);
    const snapshot = await get(invoicesRef);
    
    if (!snapshot.exists()) return [];
    
    const invoices = snapshot.val() as Record<string, NotaFiscal>;
    const invoicesArray = Object.values(invoices);
    
    // Ordenar por data de emissão (mais recente primeiro)
    invoicesArray.sort((a, b) => 
      new Date(b.dataEmissao).getTime() - new Date(a.dataEmissao).getTime()
    );
    
    return invoicesArray.slice(0, limit);
  } catch (error) {
    console.error('Erro ao buscar notas fiscais:', error);
    return [];
  }
}

export async function updateInvoice(
  nutritionistId: string,
  invoiceId: string,
  updates: Partial<NotaFiscal>
): Promise<void> {
  if (!nutritionistId || !invoiceId) {
    throw new Error('Dados inválidos');
  }

  const invoiceRef = ref(database, `users/${nutritionistId}/invoices/${invoiceId}`);
  const updatesWithTimestamp = removeUndefined({
    ...updates,
    updatedAt: new Date().toISOString(),
  });

  await set(invoiceRef, updatesWithTimestamp);
}

// ===== Relatórios para IR =====

export async function getIRReport(
  nutritionistId: string,
  year: number
): Promise<ReceitaIR[]> {
  if (!nutritionistId) return [];

  try {
    const consultationsRef = ref(database, `users/${nutritionistId}/consultations`);
    const snapshot = await get(consultationsRef);
    
    if (!snapshot.exists()) return [];
    
    const consultations = snapshot.val() as Record<string, Record<string, any>>;
    const receitasPorMes: Record<string, ReceitaIR> = {};
    
    // Iterar sobre todos os pacientes
    for (const [patientId, patientConsultations] of Object.entries(consultations)) {
      // Iterar sobre todas as consultas do paciente
      for (const [consultationId, consultationData] of Object.entries(patientConsultations)) {
        // Buscar dados financeiros
        const financialRef = ref(
          database,
          `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
        );
        const financialSnap = await get(financialRef);
        
        if (!financialSnap.exists()) continue;
        
        const financial = financialSnap.val();
        const valor = safeNumber(financial.valor, 0);
        const pago = financial.pago === true;
        
        // Só incluir se tiver valor e estiver pago
        if (valor <= 0 || !pago) continue;
        
        // Determinar data da consulta
        const consultDate = consultationData.date || 
                           consultationData.createdAt || 
                           consultationData.timestamp;
        if (!consultDate) continue;
        
        const date = new Date(consultDate);
        if (isNaN(date.getTime())) continue;
        
        // Verificar se é do ano solicitado
        if (date.getFullYear() !== year) continue;
        
        // Criar chave do mês (YYYY-MM)
        const monthKey = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // Buscar nome do paciente
        let pacienteNome = 'Paciente';
        try {
          const userSnap = await get(ref(database, `users/${patientId}`));
          if (userSnap.exists()) {
            const userData = userSnap.val() as any;
            pacienteNome = safeString(userData.name, 'Paciente');
          }
        } catch {
          // Ignora erro
        }
        
        // Inicializar mês se não existir
        if (!receitasPorMes[monthKey]) {
          receitasPorMes[monthKey] = {
            periodo: monthKey,
            total: 0,
            consultas: 0,
            media: 0,
            detalhes: [],
          };
        }
        
        // Adicionar receita
        receitasPorMes[monthKey].total += valor;
        receitasPorMes[monthKey].consultas += 1;
        receitasPorMes[monthKey].detalhes.push({
          data: date.toISOString(),
          pacienteNome,
          valor,
          tipo: consultationData.type === 'initial' ? 'primeira' : 'retorno',
        });
      }
    }
    
    // Calcular médias e ordenar
    const receitas = Object.values(receitasPorMes).map(receita => ({
      ...receita,
      media: receita.consultas > 0 ? receita.total / receita.consultas : 0,
    }));
    
    // Ordenar por período
    receitas.sort((a, b) => a.periodo.localeCompare(b.periodo));
    
    return receitas;
  } catch (error) {
    console.error('Erro ao gerar relatório IR:', error);
    return [];
  }
}

// ===== Estatísticas Financeiras =====

// Manter interface antiga para compatibilidade
export interface FinancialStats {
  receitaTotal: number;
  receitaMes: number;
  receitaAno: number;
  consultasPagas: number;
  consultasPendentes: number;
  mediaPorConsulta: number;
  consultasComValor: number;
  despesaTotal?: number;
  despesaMes?: number;
  despesaAno?: number;
  saldoLiquido?: number;
  saldoLiquidoMes?: number;
  saldoLiquidoAno?: number;
  totalTransacoes?: number;
  transacoesMes?: number;
  transacoesAno?: number;
}

export async function getFinancialStats(
  nutritionistId: string,
  month?: number,
  year?: number
): Promise<FinancialStats> {
  if (!nutritionistId) {
    return {
      receitaTotal: 0,
      receitaMes: 0,
      receitaAno: 0,
      consultasPagas: 0,
      consultasPendentes: 0,
      mediaPorConsulta: 0,
      consultasComValor: 0,
    };
  }

  const now = new Date();
  const currentMonth = month ?? now.getMonth() + 1;
  const currentYear = year ?? now.getFullYear();

  try {
    const consultationsRef = ref(database, `users/${nutritionistId}/consultations`);
    const snapshot = await get(consultationsRef);
    
    if (!snapshot.exists()) {
      return {
        receitaTotal: 0,
        receitaMes: 0,
        receitaAno: 0,
        consultasPagas: 0,
        consultasPendentes: 0,
        mediaPorConsulta: 0,
        consultasComValor: 0,
      };
    }
    
    const consultations = snapshot.val() as Record<string, Record<string, any>>;
    let receitaTotal = 0;
    let receitaMes = 0;
    let receitaAno = 0;
    let consultasPagas = 0;
    let consultasPendentes = 0;
    let consultasComValor = 0;
    
    // Iterar sobre todos os pacientes
    for (const [patientId, patientConsultations] of Object.entries(consultations)) {
      // Iterar sobre todas as consultas do paciente
      for (const [consultationId, consultationData] of Object.entries(patientConsultations)) {
        // Buscar dados financeiros
        const financialRef = ref(
          database,
          `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
        );
        const financialSnap = await get(financialRef);
        
        if (!financialSnap.exists()) continue;
        
        const financial = financialSnap.val();
        const valor = safeNumber(financial.valor, 0);
        const pago = financial.pago === true;
        
        if (valor > 0) {
          consultasComValor++;
          if (pago) {
            receitaTotal += valor;
            consultasPagas++;
            
            // Determinar data da consulta
            const consultDate = consultationData.date || 
                               consultationData.createdAt || 
                               consultationData.timestamp;
            if (consultDate) {
              const date = new Date(consultDate);
              if (!isNaN(date.getTime())) {
                if (date.getFullYear() === currentYear) {
                  receitaAno += valor;
                  if (date.getMonth() + 1 === currentMonth) {
                    receitaMes += valor;
                  }
                }
              }
            }
          } else {
            consultasPendentes++;
          }
        }
      }
    }
    
    const mediaPorConsulta = consultasPagas > 0 ? receitaTotal / consultasPagas : 0;
    
    return {
      receitaTotal,
      receitaMes,
      receitaAno,
      consultasPagas,
      consultasPendentes,
      mediaPorConsulta,
      consultasComValor,
    };
  } catch (error) {
    console.error('Erro ao calcular estatísticas financeiras:', error);
    return {
      receitaTotal: 0,
      receitaMes: 0,
      receitaAno: 0,
      consultasPagas: 0,
      consultasPendentes: 0,
      mediaPorConsulta: 0,
      consultasComValor: 0,
    };
  }
}

// ===== Transações Financeiras =====

export async function createTransaction(
  nutritionistId: string,
  transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  const transactionsRef = ref(database, `users/${nutritionistId}/transactions`);
  const newTransactionRef = push(transactionsRef);
  const transactionId = newTransactionRef.key!;

  if (!transactionId) throw new Error('Falha ao gerar ID da transação');

  const now = new Date().toISOString();
  await set(newTransactionRef, {
    ...transaction,
    id: transactionId,
    createdAt: now,
    updatedAt: now,
  });

  // Atualizar saldo da conta se especificada
  if (transaction.accountId) {
    await updateAccountBalance(nutritionistId, transaction.accountId, transaction.type === 'receita' ? transaction.amount : -transaction.amount);
  }

  return transactionId;
}

export async function getTransaction(
  nutritionistId: string,
  transactionId: string
): Promise<Transaction | null> {
  if (!nutritionistId || !transactionId) return null;

  try {
    const transactionRef = ref(database, `users/${nutritionistId}/transactions/${transactionId}`);
    const snapshot = await get(transactionRef);
    
    if (!snapshot.exists()) return null;
    
    return snapshot.val() as Transaction;
  } catch (error) {
    console.error('Erro ao buscar transação:', error);
    return null;
  }
}

export async function getTransactions(
  nutritionistId: string,
  filters?: {
    type?: 'receita' | 'despesa';
    categoryId?: string;
    accountId?: string;
    startDate?: string;
    endDate?: string;
    status?: 'pendente' | 'pago' | 'cancelado';
    limit?: number;
  }
): Promise<Transaction[]> {
  if (!nutritionistId) return [];

  try {
    const transactionsRef = ref(database, `users/${nutritionistId}/transactions`);
    const snapshot = await get(transactionsRef);
    
    if (!snapshot.exists()) return [];
    
    let transactions = Object.values(snapshot.val() as Record<string, Transaction>);
    
    // Aplicar filtros
    if (filters) {
      if (filters.type) {
        transactions = transactions.filter(t => t.type === filters.type);
      }
      if (filters.categoryId) {
        transactions = transactions.filter(t => t.categoryId === filters.categoryId);
      }
      if (filters.accountId) {
        transactions = transactions.filter(t => t.accountId === filters.accountId);
      }
      if (filters.status) {
        transactions = transactions.filter(t => t.status === filters.status);
      }
      if (filters.startDate) {
        transactions = transactions.filter(t => t.date >= filters.startDate!);
      }
      if (filters.endDate) {
        transactions = transactions.filter(t => t.date <= filters.endDate!);
      }
    }
    
    // Ordenar por data (mais recente primeiro)
    transactions.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    if (filters?.limit) {
      transactions = transactions.slice(0, filters.limit);
    }
    
    return transactions;
  } catch (error) {
    console.error('Erro ao buscar transações:', error);
    return [];
  }
}

export async function updateTransaction(
  nutritionistId: string,
  transactionId: string,
  updates: Partial<Transaction>
): Promise<void> {
  if (!nutritionistId || !transactionId) {
    throw new Error('Dados inválidos');
  }

  // Buscar transação atual para reverter saldo se necessário
  const currentTransaction = await getTransaction(nutritionistId, transactionId);
  if (currentTransaction?.accountId) {
    const oldAmount = currentTransaction.type === 'receita' ? currentTransaction.amount : -currentTransaction.amount;
    await updateAccountBalance(nutritionistId, currentTransaction.accountId, -oldAmount);
  }

  const transactionRef = ref(database, `users/${nutritionistId}/transactions/${transactionId}`);
  const updatesWithTimestamp = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await set(transactionRef, { ...currentTransaction, ...updatesWithTimestamp });

  // Atualizar saldo da conta se especificada
  if (updates.accountId || currentTransaction?.accountId) {
    const accountId = updates.accountId || currentTransaction?.accountId;
    const newAmount = updates.amount !== undefined ? updates.amount : currentTransaction?.amount || 0;
    const type = updates.type || currentTransaction?.type || 'receita';
    const amount = type === 'receita' ? newAmount : -newAmount;
    await updateAccountBalance(nutritionistId, accountId!, amount);
  }
}

export async function deleteTransaction(
  nutritionistId: string,
  transactionId: string
): Promise<void> {
  if (!nutritionistId || !transactionId) {
    throw new Error('Dados inválidos');
  }

  // Buscar transação para reverter saldo
  const transaction = await getTransaction(nutritionistId, transactionId);
  if (transaction?.accountId) {
    const amount = transaction.type === 'receita' ? -transaction.amount : transaction.amount;
    await updateAccountBalance(nutritionistId, transaction.accountId, amount);
  }

  const transactionRef = ref(database, `users/${nutritionistId}/transactions/${transactionId}`);
  await remove(transactionRef);
}

// ===== Categorias =====

const DEFAULT_CATEGORIES: Omit<TransactionCategory, 'id' | 'createdAt' | 'updatedAt'>[] = [
  // Receitas
  { name: 'Consultas', type: 'receita', isDefault: true, color: '#10b981' },
  { name: 'Cursos Vendidos', type: 'receita', isDefault: true, color: '#3b82f6' },
  { name: 'Outros Serviços', type: 'receita', isDefault: true, color: '#8b5cf6' },
  { name: 'Investimentos', type: 'receita', isDefault: true, color: '#06b6d4' },
  // Despesas
  { name: 'Consultório - Aluguel', type: 'despesa', isDefault: true, color: '#ef4444' },
  { name: 'Consultório - Energia', type: 'despesa', isDefault: true, color: '#f59e0b' },
  { name: 'Consultório - Água', type: 'despesa', isDefault: true, color: '#f59e0b' },
  { name: 'Cursos - Educação', type: 'despesa', isDefault: true, color: '#6366f1' },
  { name: 'Material', type: 'despesa', isDefault: true, color: '#ec4899' },
  { name: 'Marketing', type: 'despesa', isDefault: true, color: '#14b8a6' },
  { name: 'Transporte', type: 'despesa', isDefault: true, color: '#84cc16' },
  { name: 'Alimentação', type: 'despesa', isDefault: true, color: '#f97316' },
  { name: 'Outros', type: 'despesa', isDefault: true, color: '#64748b' },
];

export async function initializeDefaultCategories(nutritionistId: string): Promise<void> {
  if (!nutritionistId) return;

  try {
    const categoriesRef = ref(database, `users/${nutritionistId}/categories`);
    const snapshot = await get(categoriesRef);
    
    if (snapshot.exists()) {
      // Já existem categorias, não inicializar
      return;
    }

    const now = new Date().toISOString();
    const categories: Record<string, TransactionCategory> = {};
    
    for (const category of DEFAULT_CATEGORIES) {
      const categoryRef = push(categoriesRef);
      const categoryId = categoryRef.key!;
      categories[categoryId] = {
        ...category,
        id: categoryId,
        createdAt: now,
        updatedAt: now,
      };
    }

    await set(categoriesRef, categories);
  } catch (error) {
    console.error('Erro ao inicializar categorias padrão:', error);
  }
}

export async function createCategory(
  nutritionistId: string,
  category: Omit<TransactionCategory, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  await initializeDefaultCategories(nutritionistId);

  const categoriesRef = ref(database, `users/${nutritionistId}/categories`);
  const newCategoryRef = push(categoriesRef);
  const categoryId = newCategoryRef.key!;

  if (!categoryId) throw new Error('Falha ao gerar ID da categoria');

  const now = new Date().toISOString();
  await set(newCategoryRef, {
    ...category,
    id: categoryId,
    isDefault: false,
    createdAt: now,
    updatedAt: now,
  });

  return categoryId;
}

export async function getCategories(
  nutritionistId: string,
  type?: 'receita' | 'despesa'
): Promise<TransactionCategory[]> {
  if (!nutritionistId) {
    await initializeDefaultCategories(nutritionistId);
    return [];
  }

  try {
    await initializeDefaultCategories(nutritionistId);
    
    const categoriesRef = ref(database, `users/${nutritionistId}/categories`);
    const snapshot = await get(categoriesRef);
    
    if (!snapshot.exists()) return [];
    
    let categories = Object.values(snapshot.val() as Record<string, TransactionCategory>);
    
    if (type) {
      categories = categories.filter(c => c.type === type);
    }
    
    // Ordenar: padrões primeiro, depois por nome
    categories.sort((a, b) => {
      if (a.isDefault !== b.isDefault) {
        return a.isDefault ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    
    return categories;
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
    return [];
  }
}

export async function updateCategory(
  nutritionistId: string,
  categoryId: string,
  updates: Partial<TransactionCategory>
): Promise<void> {
  if (!nutritionistId || !categoryId) {
    throw new Error('Dados inválidos');
  }

  const categoryRef = ref(database, `users/${nutritionistId}/categories/${categoryId}`);
  const currentCategory = await get(ref(database, `users/${nutritionistId}/categories/${categoryId}`));
  
  if (!currentCategory.exists()) {
    throw new Error('Categoria não encontrada');
  }

  const categoryData = currentCategory.val() as TransactionCategory;
  if (categoryData.isDefault) {
    throw new Error('Não é possível editar categorias padrão');
  }

  const updatesWithTimestamp = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await set(categoryRef, { ...categoryData, ...updatesWithTimestamp });
}

export async function deleteCategory(
  nutritionistId: string,
  categoryId: string
): Promise<void> {
  if (!nutritionistId || !categoryId) {
    throw new Error('Dados inválidos');
  }

  const categoryRef = ref(database, `users/${nutritionistId}/categories/${categoryId}`);
  const snapshot = await get(categoryRef);
  
  if (!snapshot.exists()) {
    throw new Error('Categoria não encontrada');
  }

  const category = snapshot.val() as TransactionCategory;
  if (category.isDefault) {
    throw new Error('Não é possível excluir categorias padrão');
  }

  // Verificar se há transações usando esta categoria
  const transactions = await getTransactions(nutritionistId, { categoryId });
  if (transactions.length > 0) {
    throw new Error('Não é possível excluir categoria com transações associadas');
  }

  await remove(categoryRef);
}

// ===== Contas Financeiras =====

export async function createAccount(
  nutritionistId: string,
  account: Omit<FinancialAccount, 'id' | 'currentBalance' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  const accountsRef = ref(database, `users/${nutritionistId}/accounts`);
  const newAccountRef = push(accountsRef);
  const accountId = newAccountRef.key!;

  if (!accountId) throw new Error('Falha ao gerar ID da conta');

  const now = new Date().toISOString();
  await set(newAccountRef, {
    ...account,
    id: accountId,
    currentBalance: account.initialBalance,
    createdAt: now,
    updatedAt: now,
  });

  return accountId;
}

export async function getAccounts(nutritionistId: string): Promise<FinancialAccount[]> {
  if (!nutritionistId) return [];

  try {
    const accountsRef = ref(database, `users/${nutritionistId}/accounts`);
    const snapshot = await get(accountsRef);
    
    if (!snapshot.exists()) return [];
    
    const accounts = Object.values(snapshot.val() as Record<string, FinancialAccount>);
    return accounts.filter(a => a.isActive).sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Erro ao buscar contas:', error);
    return [];
  }
}

export async function updateAccount(
  nutritionistId: string,
  accountId: string,
  updates: Partial<FinancialAccount>
): Promise<void> {
  if (!nutritionistId || !accountId) {
    throw new Error('Dados inválidos');
  }

  const accountRef = ref(database, `users/${nutritionistId}/accounts/${accountId}`);
  const currentAccount = await get(accountRef);
  
  if (!currentAccount.exists()) {
    throw new Error('Conta não encontrada');
  }

  const accountData = currentAccount.val() as FinancialAccount;
  const updatesWithTimestamp = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await set(accountRef, { ...accountData, ...updatesWithTimestamp });
}

async function updateAccountBalance(
  nutritionistId: string,
  accountId: string,
  amount: number
): Promise<void> {
  if (!nutritionistId || !accountId) return;

  try {
    const accountRef = ref(database, `users/${nutritionistId}/accounts/${accountId}`);
    const snapshot = await get(accountRef);
    
    if (!snapshot.exists()) return;
    
    const account = snapshot.val() as FinancialAccount;
    const newBalance = (account.currentBalance || 0) + amount;
    
    await set(accountRef, {
      ...account,
      currentBalance: newBalance,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Erro ao atualizar saldo da conta:', error);
  }
}

export async function deleteAccount(
  nutritionistId: string,
  accountId: string
): Promise<void> {
  if (!nutritionistId || !accountId) {
    throw new Error('Dados inválidos');
  }

  // Verificar se há transações usando esta conta
  const transactions = await getTransactions(nutritionistId, { accountId });
  if (transactions.length > 0) {
    throw new Error('Não é possível excluir conta com transações associadas');
  }

  const accountRef = ref(database, `users/${nutritionistId}/accounts/${accountId}`);
  await remove(accountRef);
}

// ===== Metas Orçamentárias =====

export async function createBudget(
  nutritionistId: string,
  budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');

  const budgetsRef = ref(database, `users/${nutritionistId}/budgets`);
  const newBudgetRef = push(budgetsRef);
  const budgetId = newBudgetRef.key!;

  if (!budgetId) throw new Error('Falha ao gerar ID da meta');

  const now = new Date().toISOString();
  await set(newBudgetRef, {
    ...budget,
    id: budgetId,
    createdAt: now,
    updatedAt: now,
  });

  return budgetId;
}

export async function getBudgets(
  nutritionistId: string,
  year?: number,
  month?: number
): Promise<Budget[]> {
  if (!nutritionistId) return [];

  try {
    const budgetsRef = ref(database, `users/${nutritionistId}/budgets`);
    const snapshot = await get(budgetsRef);
    
    if (!snapshot.exists()) return [];
    
    let budgets = Object.values(snapshot.val() as Record<string, Budget>);
    
    if (year) {
      budgets = budgets.filter(b => b.year === year);
    }
    if (month) {
      budgets = budgets.filter(b => b.period === 'mensal' && b.month === month);
    }
    
    return budgets;
  } catch (error) {
    console.error('Erro ao buscar metas:', error);
    return [];
  }
}

export async function updateBudget(
  nutritionistId: string,
  budgetId: string,
  updates: Partial<Budget>
): Promise<void> {
  if (!nutritionistId || !budgetId) {
    throw new Error('Dados inválidos');
  }

  const budgetRef = ref(database, `users/${nutritionistId}/budgets/${budgetId}`);
  const currentBudget = await get(budgetRef);
  
  if (!currentBudget.exists()) {
    throw new Error('Meta não encontrada');
  }

  const budgetData = currentBudget.val() as Budget;
  const updatesWithTimestamp = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await set(budgetRef, { ...budgetData, ...updatesWithTimestamp });
}

export async function deleteBudget(
  nutritionistId: string,
  budgetId: string
): Promise<void> {
  if (!nutritionistId || !budgetId) {
    throw new Error('Dados inválidos');
  }

  const budgetRef = ref(database, `users/${nutritionistId}/budgets/${budgetId}`);
  await remove(budgetRef);
}

// ===== Relatórios Avançados =====

export async function getCashFlowData(
  nutritionistId: string,
  startDate: string,
  endDate: string
): Promise<CashFlowData[]> {
  const transactions = await getTransactions(nutritionistId, {
    startDate,
    endDate,
    status: 'pago',
  });

  const monthlyData: Record<string, CashFlowData> = {};

  transactions.forEach(transaction => {
    const date = new Date(transaction.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        month: monthKey,
        receitas: 0,
        despesas: 0,
        saldo: 0,
      };
    }

    if (transaction.type === 'receita') {
      monthlyData[monthKey].receitas += transaction.amount;
    } else {
      monthlyData[monthKey].despesas += transaction.amount;
    }
  });

  const result = Object.values(monthlyData).map(data => ({
    ...data,
    saldo: data.receitas - data.despesas,
  }));

  result.sort((a, b) => a.month.localeCompare(b.month));
  return result;
}

export async function getCategorySummary(
  nutritionistId: string,
  startDate?: string,
  endDate?: string,
  type?: 'receita' | 'despesa'
): Promise<CategorySummary[]> {
  const filters: any = { status: 'pago' };
  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;
  if (type) filters.type = type;

  const transactions = await getTransactions(nutritionistId, filters);
  const categoryMap: Record<string, CategorySummary> = {};
  let total = 0;

  transactions.forEach(transaction => {
    if (!categoryMap[transaction.categoryId]) {
      categoryMap[transaction.categoryId] = {
        categoryId: transaction.categoryId,
        categoryName: transaction.categoryName,
        type: transaction.type,
        total: 0,
        count: 0,
        percentage: 0,
      };
    }

    categoryMap[transaction.categoryId].total += transaction.amount;
    categoryMap[transaction.categoryId].count += 1;
    total += transaction.amount;
  });

  const result = Object.values(categoryMap);
  result.forEach(cat => {
    cat.percentage = total > 0 ? (cat.total / total) * 100 : 0;
  });

  result.sort((a, b) => b.total - a.total);
  return result;
}

export async function getMonthlyReport(
  nutritionistId: string,
  month: string // YYYY-MM
): Promise<MonthlyReport | null> {
  const startDate = `${month}-01`;
  const endDate = `${month}-31`;

  const transactions = await getTransactions(nutritionistId, {
    startDate,
    endDate,
  });

  const receitas = transactions
    .filter(t => t.type === 'receita' && t.status === 'pago')
    .reduce((sum, t) => sum + t.amount, 0);

  const despesas = transactions
    .filter(t => t.type === 'despesa' && t.status === 'pago')
    .reduce((sum, t) => sum + t.amount, 0);

  const categoryBreakdown = await getCategorySummary(nutritionistId, startDate, endDate);

  return {
    month,
    receitas,
    despesas,
    saldo: receitas - despesas,
    transactions,
    categoryBreakdown,
  };
}

// ===== Estatísticas Expandidas =====

export async function getExpandedFinancialStats(
  nutritionistId: string,
  month?: number,
  year?: number
): Promise<ExpandedFinancialStats> {
  const now = new Date();
  const currentMonth = month ?? now.getMonth() + 1;
  const currentYear = year ?? now.getFullYear();

  const startOfMonth = new Date(currentYear, currentMonth - 1, 1).toISOString().split('T')[0];
  const endOfMonth = new Date(currentYear, currentMonth, 0, 23, 59, 59).toISOString().split('T')[0];
  const startOfYear = new Date(currentYear, 0, 1).toISOString().split('T')[0];
  const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString().split('T')[0];

  // Buscar todas as transações
  const allTransactions = await getTransactions(nutritionistId);
  const monthTransactions = await getTransactions(nutritionistId, {
    startDate: startOfMonth,
    endDate: endOfMonth,
  });
  const yearTransactions = await getTransactions(nutritionistId, {
    startDate: startOfYear,
    endDate: endOfYear,
  });

  // Calcular receitas e despesas
  const calculateTotals = (transactions: Transaction[]) => {
    const receitas = transactions
      .filter(t => t.type === 'receita' && t.status === 'pago')
      .reduce((sum, t) => sum + t.amount, 0);
    const despesas = transactions
      .filter(t => t.type === 'despesa' && t.status === 'pago')
      .reduce((sum, t) => sum + t.amount, 0);
    return { receitas, despesas };
  };

  const allTotals = calculateTotals(allTransactions);
  const monthTotals = calculateTotals(monthTransactions);
  const yearTotals = calculateTotals(yearTransactions);

  // Buscar estatísticas de consultas (mantendo compatibilidade)
  const consultationStats = await getFinancialStats(nutritionistId, month, year);

  return {
    ...consultationStats,
    despesaTotal: allTotals.despesas,
    despesaMes: monthTotals.despesas,
    despesaAno: yearTotals.despesas,
    saldoLiquido: allTotals.receitas - allTotals.despesas,
    saldoLiquidoMes: monthTotals.receitas - monthTotals.despesas,
    saldoLiquidoAno: yearTotals.receitas - yearTotals.despesas,
    totalTransacoes: allTransactions.length,
    transacoesMes: monthTransactions.length,
    transacoesAno: yearTransactions.length,
  };
}

// ===== Integração com Consultas =====

export async function syncConsultationsAsTransactions(
  nutritionistId: string
): Promise<number> {
  if (!nutritionistId) return 0;

  try {
    // Buscar todas as consultas
    const consultationsRef = ref(database, `users/${nutritionistId}/consultations`);
    const consultationsSnapshot = await get(consultationsRef);
    
    if (!consultationsSnapshot.exists()) return 0;

    // Buscar categoria padrão de consultas
    const categories = await getCategories(nutritionistId, 'receita');
    const consultaCategory = categories.find(c => c.name === 'Consultas' || (c.isDefault && c.type === 'receita'));
    
    if (!consultaCategory) {
      console.warn('Categoria de consultas não encontrada');
      return 0;
    }

    // Buscar transações existentes relacionadas a consultas
    const existingTransactions = await getTransactions(nutritionistId);
    const consultationTransactionMap = new Map<string, Transaction>();
    existingTransactions
      .filter(t => t.relatedConsultationId)
      .forEach(t => {
        consultationTransactionMap.set(t.relatedConsultationId!, t);
      });

    const consultations = consultationsSnapshot.val() as Record<string, Record<string, any>>;
    let syncedCount = 0;
    let updatedCount = 0;

    // Iterar sobre todas as consultas
    for (const [patientId, patientConsultations] of Object.entries(consultations)) {
      for (const [consultationId, consultationData] of Object.entries(patientConsultations)) {
        const fullConsultationId = `${patientId}_${consultationId}`;
        const existingTransaction = consultationTransactionMap.get(fullConsultationId);

        // Buscar dados financeiros da consulta
        const financialRef = ref(
          database,
          `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
        );
        const financialSnap = await get(financialRef);
        
        if (!financialSnap.exists()) continue;
        
        const financial = financialSnap.val();
        const valor = safeNumber(financial.valor, 0);
        const pago = financial.pago === true;
        
        // Se já existe transação, atualizar se necessário
        if (existingTransaction) {
          const needsUpdate = 
            existingTransaction.status !== (pago ? 'pago' : 'pendente') ||
            existingTransaction.amount !== valor ||
            (pago && existingTransaction.paymentDate !== (financial.dataPagamento || existingTransaction.date));
          
          if (needsUpdate && valor > 0) {
            // Atualizar transação existente
            await updateTransaction(nutritionistId, existingTransaction.id, {
              status: pago ? 'pago' : 'pendente',
              amount: valor,
              paymentDate: pago ? (financial.dataPagamento || existingTransaction.date) : undefined,
            });
            updatedCount++;
          }
          continue;
        }
        
        // Só criar transação se tiver valor e estiver pago
        if (valor <= 0 || !pago) continue;
        
        // Determinar data da consulta
        const consultDate = consultationData.date || 
                           consultationData.createdAt || 
                           consultationData.timestamp;
        if (!consultDate) continue;
        
        const date = new Date(consultDate);
        if (isNaN(date.getTime())) continue;

        // Buscar nome do paciente
        let pacienteNome = 'Paciente';
        try {
          const userSnap = await get(ref(database, `users/${patientId}`));
          if (userSnap.exists()) {
            const userData = userSnap.val() as any;
            pacienteNome = safeString(userData.name, 'Paciente');
          }
        } catch {
          // Ignora erro
        }

        // Criar transação
        const transactionType = consultationData.type === 'initial' ? 'primeira' : 'retorno';
        const description = `Consulta ${transactionType === 'primeira' ? 'Inicial' : 'Retorno'} - ${pacienteNome}`;

        await createTransaction(nutritionistId, {
          type: 'receita',
          categoryId: consultaCategory.id,
          categoryName: consultaCategory.name,
          amount: valor,
          description,
          date: date.toISOString(),
          status: pago ? 'pago' : 'pendente',
          paymentDate: financial.dataPagamento || date.toISOString(),
          relatedConsultationId: fullConsultationId,
        });

        syncedCount++;
      }
    }

    return syncedCount + updatedCount;

    return syncedCount;
  } catch (error) {
    console.error('Erro ao sincronizar consultas como transações:', error);
    return 0;
  }
}
